import {
  useMutation,
  useQuery,
  QueryClient,
  useQueryClient,
} from "@tanstack/react-query";
import { useShallow } from "zustand/shallow";
import { useUIStore } from "../stores/ui-store";
import { UIState } from "../types/ui-types";
import {
  createAkeneoModel,
  fetchAkeneoModels,
  updateAkeneoModel,
} from "../api/akeneoModels.api";
import {
  CreateAkeneoModelPaylod,
  UpdateAkeneoModelPaylod,
} from "../api/models/akeneoModels.models";
import { useTranslation } from "react-i18next";
import { useEffect } from "react";

interface UseModelsProps {
  companyId?: number;
}

const useModels = ({ companyId }: UseModelsProps) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [bearerToken, toast] = useUIStore(
    useShallow((state: UIState) => [state.bearerToken, state.toastRef])
  );

  const { data, error, isLoading, refetch } = useQuery({
    queryKey: ["akeneoModels", companyId],
    queryFn: () => fetchAkeneoModels(bearerToken, companyId),
    enabled: !!companyId,
  });

  // referch when companyId changes
  useEffect(() => {
    if (companyId) {
      refetch();
    }
  }, [companyId, refetch]);

  const fetchModels = async () => {
    await refetch();
  };

  // Create new model
  const createNewModel = async (company: CreateAkeneoModelPaylod) => {
    const result = await createAkeneoModelMutation.mutateAsync(company);
    return result;
  };

  const createAkeneoModelMutation = useMutation({
    mutationFn: (payload: CreateAkeneoModelPaylod) =>
      createAkeneoModel(bearerToken as string, payload),
    onError: (error) => {
      console.error("Failed to create akeneo model:", error);
      toast?.current?.show({
        severity: "error",
        detail: t("text-model-create-failed"),
        life: 3000,
      });
    },
    onSuccess: () => {
      toast?.current?.show({
        severity: "success",
        detail: t("text-model-created"),
        life: 3000,
      });

      queryClient.invalidateQueries({ queryKey: ["packageRows"] });
      queryClient.invalidateQueries({
        queryKey: ["models"],
      });
    },
  });

  // Update existing model
  const updateExistingModel = async (payload: UpdateAkeneoModelPaylod) => {
    const result = await updateAkeneoModelMutation.mutateAsync(payload);
    return result;
  };

  const updateAkeneoModelMutation = useMutation({
    mutationFn: (payload: UpdateAkeneoModelPaylod) =>
      updateAkeneoModel(bearerToken as string, payload),
    onError: (error) => {
      console.error("Failed to update akeneo model:", error);
      toast?.current?.show({
        severity: "error",
        detail: t("text-model-update-failed"),
        life: 3000,
      });
    },
    onSuccess: () => {
      toast?.current?.show({
        severity: "success",
        detail: t("text-model-updated"),
        life: 3000,
      });

      queryClient.invalidateQueries({
        queryKey: ["models"],
      });
      queryClient.invalidateQueries({ queryKey: ["packageRows"] });
    },
  });

  const isCreatingModel = createAkeneoModelMutation.isPending;
  const isUpdatingModel = updateAkeneoModelMutation.isPending;

  return {
    models: data,
    error,
    isLoading,
    fetchModels,
    createNewModel,
    updateExistingModel,
    isCreatingModel,
    isUpdatingModel,
  };
};

export default useModels;
